/**
 * OtpInput — iOS-native 6-digit code field.
 *
 * Apple's official recommendation for `autocomplete="one-time-code"` is a
 * SINGLE input element. iOS reliably surfaces the QuickType suggestion only
 * when there is exactly one such field on the page. Splitting into six DOM
 * inputs is a common foot-gun that silently disables the autofill bar.
 *
 * This component therefore renders ONE invisible text input overlaying six
 * pretty glass-tile boxes. The user only sees the boxes; iOS only sees the
 * input. Best of both worlds.
 *
 * - Paste / SMS-autofill / iOS code suggestion all work natively.
 * - Auto-fires `onComplete(code)` when six digits are typed.
 * - Backspace works as expected.
 * - The single caret tracks the current empty box correctly.
 */
import { useEffect, useRef } from "react";

const LEN = 6;

export default function OtpInput({
  value = "",
  onChange,
  onComplete,
  disabled = false,
  autoFocus = true,
  testid = "otp-input",
}) {
  const inputRef = useRef(null);
  const digits = (value || "").replace(/\D/g, "").slice(0, LEN);

  // Auto-focus on mount + on any user tap on the visual boxes.
  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus();
  }, [autoFocus, disabled]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, LEN);
    onChange?.(raw);
    if (raw.length === LEN) {
      onComplete?.(raw);
      inputRef.current?.blur();
    }
  };

  const focusInput = () => inputRef.current?.focus();

  return (
    <div
      className="relative flex items-center justify-center gap-2.5 sm:gap-3 select-none"
      dir="ltr"
      data-testid={testid}
      onClick={focusInput}
    >
      {/* The real, invisible input — receives focus, autofill, and paste.
          Stretched across the entire visual area so taps on any box land
          here. `caret-transparent` hides the system caret since each tile
          renders its own pulse cursor. */}
      <input
        ref={inputRef}
        type="text"
        value={digits}
        onChange={handleChange}
        disabled={disabled}
        autoComplete="one-time-code"
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={LEN}
        aria-label="رمز التحقّق المكوّن من ستة أرقام"
        data-testid={`${testid}-field`}
        className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer caret-transparent"
      />

      {/* Visual tiles — purely decorative, mirror `digits`. The next-to-be-typed
          tile shows a blinking caret bar centred inside it. */}
      {Array.from({ length: LEN }).map((_, i) => {
        const ch = digits[i] || "";
        const isFocused = digits.length === i;
        return (
          <div
            key={i}
            data-testid={`${testid}-${i}`}
            className={[
              "relative w-11 h-14 sm:w-12 sm:h-16 rounded-2xl text-center font-tajawal",
              "flex items-center justify-center text-[24px] sm:text-[28px] font-extrabold text-[#0A192F]",
              "bg-white/70 backdrop-blur-md border-2 transition-all duration-150",
              ch
                ? "border-[#D4AF37] shadow-[0_2px_18px_rgba(212,175,55,0.25)]"
                : isFocused
                ? "border-[#D4AF37] bg-white shadow-[0_2px_22px_rgba(10,25,47,0.12)]"
                : "border-white/70",
              disabled ? "opacity-50" : "",
            ].join(" ")}
          >
            {ch || (isFocused && !disabled ? <CaretBlink /> : null)}
          </div>
        );
      })}
    </div>
  );
}

/* Centred 2px-wide blinking caret bar — perfectly inside the focused tile. */
function CaretBlink() {
  return (
    <span
      aria-hidden="true"
      className="block w-[2px] h-6 sm:h-7 bg-[#0A192F]/70 animate-pulse rounded-full"
    />
  );
}
